// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppIcon,
  Badge,
  Button,
  DataTable,
  IconButton,
  Modal,
  StatusBanner,
  TablePagination,
} from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { fundsApi, StrategyDTO, StrategyStatus } from '../../api/fundsApi';
import { computePortfolioRisk } from './portfolioRisk';
import { PortfolioRiskBadge } from './PortfolioRiskBadge';

type ConfirmAction = 'publish' | 'archive' | 'delete';

const STATUS_COLOR: Record<StrategyStatus, 'blue' | 'green' | 'grey'> = {
  DRAFT: 'blue',
  PUBLISHED: 'green',
  ARCHIVED: 'grey',
};

const CONFIRM_COPY: Record<ConfirmAction, { title: string; body: string; cta: string }> = {
  publish: {
    title: 'Publish portfolio',
    body: 'A published portfolio becomes immutable — to change it later you clone it into a new draft. Allocations must sum to 100%.',
    cta: 'Publish',
  },
  archive: {
    title: 'Archive portfolio',
    body: 'The portfolio is removed from pickers and listings. This cannot be undone from the UI.',
    cta: 'Archive',
  },
  delete: {
    title: 'Delete draft',
    body: 'The draft and its allocations are permanently deleted.',
    cta: 'Delete',
  },
};

export interface PortfolioListPanelProps {
  /** Rendered on the right of the panel header — e.g. the Portfolio Builder action. */
  headerRight?: ReactNode;
  pageSize?: number;
}

/**
 * Portfolios (fund strategies) as a self-contained card panel: list +
 * pagination + lifecycle actions (publish / archive / delete / clone).
 * Embedded on the Fund Center page above the funds table (the Portfolio
 * Builder action is passed in as headerRight). Pagination is client-side over
 * the visible set — portfolios per brokerage are few — matching the
 * TablePagination UX used by the other lists on the page.
 */
export default function PortfolioListPanel({
  headerRight,
  pageSize: initialPageSize = 5,
}: PortfolioListPanelProps) {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState<StrategyDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ action: ConfirmAction; strategy: StrategyDTO } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fundsApi.listStrategies(0, 100);
      setStrategies(data.content.filter((s) => s.status !== 'ARCHIVED'));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(strategies.length / pageSize));
  const pageRows = useMemo(
    () => strategies.slice(page * pageSize, page * pageSize + pageSize),
    [strategies, page, pageSize]
  );

  const runConfirmed = async () => {
    if (!confirm) return;
    const { action, strategy } = confirm;
    try {
      setBusy(true);
      setActionError(null);
      if (action === 'publish') {
        await fundsApi.publishStrategy(strategy.id);
        setBanner(`"${strategy.name}" published.`);
      } else if (action === 'archive') {
        await fundsApi.archiveStrategy(strategy.id);
        setBanner(`"${strategy.name}" archived.`);
      } else {
        await fundsApi.deleteStrategy(strategy.id);
        setBanner(`"${strategy.name}" deleted.`);
      }
      setConfirm(null);
      setPage(0);
      await load();
    } catch (e: unknown) {
      setActionError(extractApiMessage(e, `Failed to ${action} the portfolio`));
    } finally {
      setBusy(false);
    }
  };

  const clone = async (strategy: StrategyDTO) => {
    try {
      setError(null);
      const created = await fundsApi.cloneStrategy(strategy.id);
      navigate(`/funds/portfolios/${created.id}`);
    } catch (e: unknown) {
      setError(extractApiMessage(e, 'Failed to clone the portfolio'));
    }
  };

  const columns: DataTableColumn<StrategyDTO>[] = [
    {
      key: 'name',
      header: 'Portfolio',
      render: (s) => (
        <div>
          <div className="font-medium">{s.name}</div>
          {s.description && <div className="text-body-04 text-grey-03">{s.description}</div>}
        </div>
      ),
    },
    {
      key: 'scope',
      header: 'Scope',
      width: '110px',
      render: (s) => (
        <Badge color={s.scope === 'BROKERAGE' ? 'purple' : 'neutral'} size="sm">
          {s.scope === 'BROKERAGE' ? 'Brokerage' : 'Mine'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '110px',
      render: (s) => (
        <Badge color={STATUS_COLOR[s.status]} size="sm">
          {s.status}
        </Badge>
      ),
    },
    {
      key: 'risk',
      header: 'Risk profile',
      width: '130px',
      render: (s) => (s.riskProfile ? titleCase(s.riskProfile) : '—'),
    },
    {
      key: 'funds',
      header: 'Funds',
      width: '80px',
      render: (s) => s.components.length,
    },
    {
      key: 'riskRating',
      header: 'Risk',
      width: '120px',
      render: (s) => (
        <PortfolioRiskBadge
          result={computePortfolioRisk(
            s.components.map((c) => ({ riskRating: c.riskRating, weight: c.allocationPct ?? 0 }))
          )}
        />
      ),
    },
    {
      key: 'allocation',
      header: 'Allocation',
      width: '100px',
      render: (s) => `${allocationSum(s).toFixed(2)}%`,
    },
    {
      key: 'updated',
      header: 'Updated',
      width: '110px',
      render: (s) => (s.updatedAt ? formatDate(s.updatedAt) : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '170px',
      render: (s) => {
        const own = s.scope === 'ADVISOR';
        return (
          <span className="flex items-center gap-xs" onClick={(e) => e.stopPropagation()}>
            {own && s.status === 'DRAFT' && (
              <>
                <IconButton
                  variant="ghost"
                  aria-label={`Publish ${s.name}`}
                  onClick={() => setConfirm({ action: 'publish', strategy: s })}
                  icon={<AppIcon name="send" size={18} />}
                />
                <IconButton
                  variant="ghost"
                  aria-label={`Delete ${s.name}`}
                  onClick={() => setConfirm({ action: 'delete', strategy: s })}
                  icon={<AppIcon name="trash" size={18} />}
                />
              </>
            )}
            {own && s.status === 'PUBLISHED' && (
              <IconButton
                variant="ghost"
                aria-label={`Archive ${s.name}`}
                onClick={() => setConfirm({ action: 'archive', strategy: s })}
                icon={<AppIcon name="archive" size={18} />}
              />
            )}
            <IconButton
              variant="ghost"
              aria-label={`Clone ${s.name}`}
              onClick={() => clone(s)}
              icon={<AppIcon name="copy" size={18} />}
            />
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-lg" data-testid="portfolio-list-panel">
      <StatusBanner
        variant="success"
        message={banner}
        visible={!!banner}
        dismissible
        onDismiss={() => setBanner(null)}
        position="inline"
      />

      <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-lg">
        <div className="flex items-center justify-between gap-md flex-wrap">
          <h2 className="m-0 text-body-01 font-medium">Portfolios</h2>
          {headerRight}
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <DataTable
          columns={columns}
          data={pageRows}
          rowKey={(s) => s.id}
          onRowClick={(s) => navigate(`/funds/portfolios/${s.id}`)}
          loading={loading}
          skeletonRows={5}
          emptyState={
            <span>
              No portfolios yet — build one by selecting funds below, or start empty with the
              Portfolio Builder.
            </span>
          }
        />

        {strategies.length > 0 && (
          <TablePagination
            config={{
              currentPage: page + 1,
              totalPages,
              itemsPerPage: pageSize,
              itemsPerPageOptions: [5, 10, 20],
              onPageChange: (p) => setPage(p - 1),
              onItemsPerPageChange: (s) => {
                setPageSize(s);
                setPage(0);
              },
            }}
          />
        )}
      </div>

      {confirm && (
        <Modal open onClose={() => (busy ? undefined : setConfirm(null))} size="small">
          <Modal.Header title={CONFIRM_COPY[confirm.action].title} onClose={() => setConfirm(null)} />
          <Modal.Content>
            <p className="m-0 mb-md text-body-03">
              <span className="font-medium">{confirm.strategy.name}</span> —{' '}
              {CONFIRM_COPY[confirm.action].body}
            </p>
            {actionError && <Alert variant="error">{actionError}</Alert>}
            <div className="flex justify-end gap-sm mt-lg">
              <Button variant="outlined" size="md" onClick={() => setConfirm(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                size="md"
                colour={confirm.action === 'publish' ? 'purple' : 'red'}
                onClick={runConfirmed}
                loading={busy}
                disabled={busy}
              >
                {CONFIRM_COPY[confirm.action].cta}
              </Button>
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}

function allocationSum(s: StrategyDTO): number {
  return s.components.reduce((sum, c) => sum + (c.allocationPct ?? 0), 0);
}

function titleCase(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

function extractApiMessage(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { message?: string } } })?.response?.data;
  if (data?.message) return data.message;
  return e instanceof Error ? e.message : fallback;
}
